import { 
  AssessmentData,
  InsightsData, 
  CareData,
  PredictiveData,
  HealthChallengeData,
  HealthCoachMessage,
  HealthFocusArea,
  NutritionData,
  MealData
} from '../types/health-tools';

/**
 * Mock data for the HealthTools components
 * Used for demonstration purposes in the UI
 */
const mockHealthData = {
  // Assessment Section Data
  assessmentData: {
    calculators: [
      {
        id: 'bmi',
        name: 'BMI Calculator',
        description: 'Calculate your Body Mass Index',
        lastUsed: '2023-05-15T10:30:00Z',
        icon: 'Scale'
      },
      {
        id: 'calories',
        name: 'Calorie Needs',
        description: 'Estimate daily calorie requirements',
        lastUsed: '2023-06-02T14:20:00Z',
        icon: 'Flame'
      },
      {
        id: 'heart',
        name: 'Heart Disease Risk',
        description: 'Assess cardiovascular risk factors',
        lastUsed: '2023-04-28T09:15:00Z',
        icon: 'Heart'
      }
    ],
    riskAssessments: [
      {
        id: 'diabetes',
        name: 'Diabetes Risk',
        description: 'Evaluate your risk of developing type 2 diabetes',
        lastCompleted: '2023-05-10T16:45:00Z',
        result: 'Low Risk',
        resultScore: 15,
        maxScore: 100
      },
      {
        id: 'stroke',
        name: 'Stroke Risk',
        description: 'Assess your risk factors for stroke',
        lastCompleted: '2023-03-22T11:30:00Z',
        result: 'Moderate Risk',
        resultScore: 42,
        maxScore: 100
      }
    ],
    screeningTools: [
      {
        id: 'depression',
        name: 'Depression Screening',
        description: 'PHQ-9 depression screening questionnaire',
        lastCompleted: '2023-06-05T13:20:00Z',
        result: 'Minimal Depression',
        resultScore: 3,
        maxScore: 27
      },
      {
        id: 'anxiety',
        name: 'Anxiety Screening',
        description: 'GAD-7 anxiety screening tool',
        lastCompleted: '2023-06-05T13:35:00Z',
        result: 'Mild Anxiety',
        resultScore: 6,
        maxScore: 21
      }
    ]
  } as AssessmentData,

  // Insights Section Data
  insightsData: {
    healthScore: {
      current: 78,
      previous: 72,
      change: '+6',
      date: '2023-06-10',
      breakdown: [
        { category: 'Physical Activity', score: 82, weight: 0.3 },
        { category: 'Nutrition', score: 65, weight: 0.3 },
        { category: 'Sleep', score: 90, weight: 0.2 },
        { category: 'Stress', score: 75, weight: 0.2 }
      ]
    },
    riskFactors: [
      { name: 'Blood Pressure', status: 'Elevated', value: '135/85 mmHg', recommendation: 'Monitor regularly and consider dietary changes' },
      { name: 'BMI', status: 'Overweight', value: '27.3', recommendation: 'Aim for a 5-10% weight reduction' },
      { name: 'Sleep', status: 'Good', value: '7.5 hrs/night', recommendation: 'Maintain current sleep habits' }
    ],
    insights: [
      { title: 'Exercise Consistency', message: 'Your exercise consistency has improved by 20% over the last month.', type: 'positive' },
      { title: 'Stress Management', message: 'Stress levels increased during weekdays. Consider breathing exercises or short breaks.', type: 'suggestion' },
      { title: 'Hydration', message: 'Your average daily water intake is below recommended levels.', type: 'warning' }
    ]
  } as InsightsData,

  // Care Section Data
  careData: {
    recommendations: [
      { id: 'rec1', category: 'Preventive', title: 'Annual Physical Exam', description: 'Schedule your annual physical examination', urgency: 'Medium', dueDate: '2023-08-15' },
      { id: 'rec2', category: 'Screening', title: 'Cholesterol Screening', description: 'Due for your 5-year cholesterol screening', urgency: 'High', dueDate: '2023-07-10' },
      { id: 'rec3', category: 'Vaccination', title: 'Flu Vaccine', description: 'Annual flu vaccination is recommended', urgency: 'Medium', dueDate: '2023-09-30' }
    ],
    carePlan: {
      conditions: [
        { name: 'Hypertension', status: 'Managed', lastReviewed: '2023-05-18' }
      ],
      medications: [
        { name: 'Lisinopril', dosage: '10mg', frequency: 'Once daily', purpose: 'Blood pressure' }
      ],
      goals: [
        { description: 'Reduce blood pressure to below 130/80', target: '2023-09-01', progress: 65 },
        { description: 'Increase physical activity to 150 min/week', target: '2023-07-15', progress: 80 }
      ]
    },
    providers: [
      { name: 'Dr. Sarah Johnson', specialty: 'Primary Care', lastVisit: '2023-04-12', nextVisit: '2023-10-15' },
      { name: 'Dr. Mark Williams', specialty: 'Cardiology', lastVisit: '2023-02-23', nextVisit: '2023-08-23' }
    ]
  } as CareData,

  // Predictive Section Data
  predictiveData: {
    riskPredictions: [
      { condition: 'Type 2 Diabetes', currentRisk: 18, trend: 'decreasing', timeframe: '5 years', triggerFactors: ['Family history', 'BMI', 'Age'] },
      { condition: 'Hypertension', currentRisk: 42, trend: 'stable', timeframe: '5 years', triggerFactors: ['Diet', 'Stress levels', 'Physical activity'] },
      { condition: 'Coronary Heart Disease', currentRisk: 15, trend: 'decreasing', timeframe: '10 years', triggerFactors: ['Cholesterol levels', 'Blood pressure', 'Family history'] }
    ],
    healthProjections: [
      { metric: 'Weight', current: 185, projected: 175, timeframe: '6 months', assumedBehavior: 'Current exercise and diet plan maintained' },
      { metric: 'Resting Heart Rate', current: 72, projected: 68, timeframe: '3 months', assumedBehavior: 'Cardio exercise 3x weekly' },
      { metric: 'HbA1c', current: 5.6, projected: 5.4, timeframe: '6 months', assumedBehavior: 'Reduced sugar intake and regular exercise' }
    ],
    interventionImpacts: [
      { 
        intervention: 'Mediterranean Diet', 
        impacts: [
          { condition: 'Type 2 Diabetes', riskReduction: 25 },
          { condition: 'Hypertension', riskReduction: 15 },
          { condition: 'Heart Disease', riskReduction: 30 }
        ],
        difficulty: 'Medium',
        timeToEffect: '3-6 months'
      },
      { 
        intervention: '150 min/week Exercise', 
        impacts: [
          { condition: 'Type 2 Diabetes', riskReduction: 40 },
          { condition: 'Hypertension', riskReduction: 20 },
          { condition: 'Heart Disease', riskReduction: 35 }
        ],
        difficulty: 'Medium',
        timeToEffect: '2-4 months'
      }
    ]
  } as PredictiveData,

  // Health Challenges
  healthChallenges: [
    {
      id: 'ch1',
      title: '10K Steps Daily',
      description: 'Complete 10,000 steps every day for 30 days',
      category: 'Physical Activity',
      progress: 65,
      target: 100,
      unit: '%',
      startDate: '2023-06-01',
      endDate: '2023-06-30',
      participants: 243
    },
    {
      id: 'ch2',
      title: 'Sugar Reduction',
      description: 'Reduce daily sugar intake to less than 25g',
      category: 'Nutrition',
      progress: 80,
      target: 100,
      unit: '%',
      startDate: '2023-06-05',
      endDate: '2023-06-25',
      participants: 187
    },
    {
      id: 'ch3',
      title: 'Meditation Minutes',
      description: 'Complete 200 minutes of meditation this month',
      category: 'Mental Wellness',
      progress: 35,
      target: 100,
      unit: '%',
      startDate: '2023-06-01',
      endDate: '2023-06-30',
      participants: 156
    }
  ] as HealthChallengeData[],
  
  // Coach Messages
  coachMessages: [
    {
      sender: 'coach',
      content: 'Good morning! I noticed your sleep pattern has been inconsistent. Would you like some tips to improve sleep quality?',
      timestamp: '2023-06-10T09:15:00Z',
      suggestions: [
        'Try to go to bed at the same time each night',
        'Limit screen time 1 hour before bed',
        'Keep your bedroom cool and dark'
      ]
    },
    {
      sender: 'user',
      content: "Yes, I'd love some tips. I've been having trouble falling asleep lately.",
      timestamp: '2023-06-10T09:20:00Z'
    },
    {
      sender: 'coach',
      content: 'I recommend trying the 4-7-8 breathing technique when you get into bed. Inhale for 4 seconds, hold for 7, and exhale for 8. This can help calm your nervous system.',
      timestamp: '2023-06-10T09:22:00Z'
    }
  ] as HealthCoachMessage[],
  
  // Health Focus Areas
  healthFocusAreas: [
    {
      name: 'Stress Management',
      score: 35,
      priority: 'High Priority'
    },
    {
      name: 'Physical Activity',
      score: 65,
      priority: 'Medium Priority'
    },
    {
      name: 'Hydration',
      score: 50,
      priority: 'Medium Priority'
    }
  ] as HealthFocusArea[],
  
  // Nutrition Data
  nutritionData: {
    calories: { current: 1650, goal: 2000 },
    protein: { current: 85, goal: 120 },
    carbs: { current: 190, goal: 250 },
    fat: { current: 55, goal: 65 },
    water: { current: 5, goal: 8 }
  } as NutritionData,
  
  // Meal History
  mealHistory: [
    {
      id: 'm1',
      name: 'Breakfast - Oatmeal with berries',
      calories: 320,
      time: '2023-06-10T07:30:00Z',
      protein: 12,
      carbs: 55,
      fat: 6
    },
    {
      id: 'm2',
      name: 'Lunch - Grilled chicken salad',
      calories: 430,
      time: '2023-06-10T12:15:00Z',
      protein: 35,
      carbs: 25,
      fat: 22
    },
    {
      id: 'm3',
      name: 'Snack - Apple and almonds',
      calories: 210,
      time: '2023-06-10T15:30:00Z',
      protein: 5,
      carbs: 30,
      fat: 10
    }
  ] as MealData[],
  
  // Nutrition Recommendations
  nutritionRecommendations: [
    'Try to increase protein intake with lean sources like chicken, fish, or plant-based options',
    'Consider adding more fiber-rich vegetables to your lunch and dinner',
    'Your hydration is below target - aim for 8 cups of water daily'
  ]
};

export default mockHealthData; 